import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { useMsal } from '@azure/msal-react';
import styles from './Dashboard.module.scss';
import { getCookie } from '../../utils/helpers';
import configData from '../../config.json';
import { fetchUserData } from '../../actions/user';
import DashboardCardLayout from '../../Components/common/Cards/DashboardCardLayout/DashboardCardLayout';
import { contractorConfig, approverConfig } from './DashboardRoleConfig';
import { useWindowSize } from '../../utils/hooks';
import { responsiveSizeTimesheet } from '../../utils/constants';
import { getTandEPendingCounts, getTimeExpenseValidation, getTandECountsContractor } from '../../actions/timesheet';
import { idTokenClaims } from '../../utils/enums';
import { shouldReadExpensesNotification, shouldReadTimesheetsNotification } from '../../actions/appNotifications';

export function Dashboard() {
  const user = useSelector((state) => state?.user?.profile);
  const { accounts } = useMsal();
  const { t } = useTranslation();
  const appInsights = useAppInsightsContext();
  const { width } = useWindowSize();
  const userId = localStorage.getItem('UserId');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchField, setSearchField] = useState(null);
  const accessTo = useSelector((state) => state.accessToParts);

  const pendingTandECounts = useSelector((state) => state.timesheet?.tandecounts);
  const timesheetValidationStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidAdminSettings);
  const expenseValidationStatus = useSelector((state) => state?.timesheet?.validate?.expense.IsValidAdminSettings);
  const timesheetPlacementStatus = useSelector((state) => state?.timesheet?.validate?.timesheet.IsValidPlacementStatus);
  const expensePlacementStatus = useSelector((state) => state?.timesheet?.validate?.expense.IsValidPlacementStatus);
  const validationPending = useSelector((state) => state?.timesheet?.validatePending);
  const timesheetCountsContractor = useSelector((state) => state?.timesheet?.contractortandecounts?.Timesheet);
  const expensesCountsContractor = useSelector((state) => state?.timesheet?.contractortandecounts?.Expense);

  const trackEvent = useTrackEvent(appInsights, `onboarding claims value userId: ${userId} `, accessTo.onboarding);

  const approverConfigParams = {
    t,
    navigate,
    setSearchField,
    timesheetValidationStatus,
    expenseValidationStatus,
    validationPending,
    pendingTandECounts
  };

  const handleTimesheetStatusClick = (e, statusType, count) => {
    if (count > 0) {
      dispatch(shouldReadTimesheetsNotification({ enabled: true, status: statusType }));
    }

    e.stopPropagation();
  };

  const handleExpenseStatusClick = (e, statusType, count) => {
    if (count > 0) {
      dispatch(shouldReadExpensesNotification({ enabled: true, status: statusType }));
    }

    e.stopPropagation();
  };

  const contractorConfigParam = {
    t,
    navigate,
    setSearchField,
    searchField,
    accessTo,
    timesheetValidationStatus,
    expenseValidationStatus,
    timesheetPlacementStatus,
    expensePlacementStatus,
    timesheetCountsContractor,
    expensesCountsContractor,
    validationPending,
    handleTimesheetStatusClick,
    handleExpenseStatusClick
  };

  const getConfigByRole = () => {
    // approver (he never has onboarding)
    if (accessTo.tAndEApprover) {
      if (width > responsiveSizeTimesheet) {
        return approverConfig(approverConfigParams);
      }
      const configs = approverConfig(approverConfigParams);
      const contractorSearchConfig = configs.splice(
        configs.findIndex((config) => config.id === 'contractor'),
        1
      );
      return [contractorSearchConfig[0], ...configs];
    }

    if (accessTo.tAndEContractor && accessTo.onboarding) {
      return contractorConfig(contractorConfigParam);
    }

    if (accessTo.tAndEContractor && !accessTo.onboarding) {
      const configs = contractorConfig(contractorConfigParam);
      configs.splice(
        configs.findIndex((config) => config.id === 'onboarding'),
        1
      );
      return configs;
    }

    // simple user WiTH onboarding
    if (accessTo.onboarding && !accessTo.tAndEApprover && !accessTo.tAndEContractor) {
      const configs = contractorConfig(contractorConfigParam);
      configs.splice(
        configs.findIndex((config) => config.id === 'notification'),
        1
      );
      configs.splice(
        configs.findIndex((config) => config.id === 'timesheet'),
        1
      );
      configs.splice(
        configs.findIndex((config) => config.id === 'expense'),
        1
      );
      return configs;
    }
    // simple user WiTHOUT onboarding
    if (!accessTo.onboarding) {
      const configs = contractorConfig(contractorConfigParam);
      configs.splice(
        configs.findIndex((config) => config.id === 'onboarding'),
        1
      );
      configs.splice(
        configs.findIndex((config) => config.id === 'notification'),
        1
      );
      configs.splice(
        configs.findIndex((config) => config.id === 'timesheet'),
        1
      );
      configs.splice(
        configs.findIndex((config) => config.id === 'expense'),
        1
      );
      return configs;
    }

    return contractorConfig(contractorConfigParam);
  };

  useEffect(() => {
    if (userId && !user) {
      dispatch(fetchUserData(userId));
    }

    if (accessTo.tAndEApprover) {
      dispatch(getTandEPendingCounts());
    }

    if (accessTo.tAndEContractor) {
      dispatch(getTandECountsContractor());
    }
  }, [userId, accessTo.tAndEApprover, accessTo.tAndEContractor, validationPending]);

  useEffect(() => {
    const claims = accounts[0]?.idTokenClaims;
    trackEvent({
      props: {
        user: user?.Id || userId,
        cookieValue: getCookie(configData.TIFO_ONBOARDING_KEY),
        onboarding: claims[idTokenClaims.IS_TIFO_USER],
        tAndEContractor: claims[idTokenClaims.IS_TNE_CONTRACTOR],
        tAndEApprover: claims[idTokenClaims.IS_TNE_APPROVER]
      }
    });
    if (claims[idTokenClaims.IS_TNE_CONTRACTOR] || claims[idTokenClaims.IS_TNE_APPROVER]) {
      dispatch(getTimeExpenseValidation('', ''));
    }
  }, []);

  return (
    <div className={styles.dashboard}>
      <div className="row">
        <div className="col-12 col-md-6">
          {accounts?.length && (
            <h1 className='text-2xl md:text-4xl'>
              {t('dashboard.slider.hi')} {accounts[0]?.idTokenClaims?.given_name}!
            </h1>
          )}
        </div>
      </div>
      <div className={styles.row}>
        {getConfigByRole()
          .sort((a, b) => a.order - b.order)
          .map((config) => {
            return (
              <DashboardCardLayout
                icon={config.icon}
                title={config.title}
                isDualLayout={config.isDualLayout}
                isCustom={config.isCustom}
                cardSize={config.cardSize}
                leftContent={config.leftContent}
                key={config.id}
                clickAction={config.clickAction}
                extraComponent={config?.extraComponent}
              >
                {config?.child(styles)}
              </DashboardCardLayout>
            );
          })}
      </div>
    </div>
  );
}
